import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountWebLayout, AccountPageHeader, AccountTabBar, AccountStatStrip } from './AccountWebLayout';
import { fetchCreditsProfile, formatCreditDate } from '../../utils/creditsApi';
import { CreditTransactionDetails } from '../CreditTransactionDetails';

const EARN_INFO = [
    {
        icon: 'bi-gift-fill',
        title: 'Welcome bonus',
        text: '₹200 store credit when you register and verify your account.',
    },
    {
        icon: 'bi-bag-check-fill',
        title: 'Order cashback',
        text: '5% store credit on delivered orders — credited 7 days after delivery. Higher rates for VIP.',
    },
    {
        icon: 'bi-award-fill',
        title: 'VIP membership',
        text: 'Gold and Platinum tiers earn up to 1.5× cashback.',
    },
];

export function MyCredits() {
    const navigate = useNavigate();
    const [activityTab, setActivityTab] = useState('all');
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isGuest =
        localStorage.getItem('guestUser') === 'true' || !localStorage.getItem('token');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isGuest) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const type =
                    activityTab === 'earned' ? 'earned' : activityTab === 'used' ? 'used' : undefined;
                const data = await fetchCreditsProfile({ type });
                if (!cancelled) setProfile(data);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load store credit. Please try again.');
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isGuest, activityTab]);

    const transactions = useMemo(() => {
        const list = profile?.transactions ?? [];
        return list.map((txn) => ({
            ...txn,
            dateLabel: formatCreditDate(txn.date),
        }));
    }, [profile]);

    const balance = profile?.balance ?? 0;

    if (isGuest) {
        return (
            <AccountWebLayout title="My Credit">
                <AccountPageHeader title="My Credit" description="Sign in to view and use your store credit." />
                <div className="account-web-notice">
                    <p>Store credit is available for registered customers.</p>
                    <button
                        type="button"
                        className="btn-primary--sm"
                        onClick={() => navigate('/auth/login')}
                    >
                        Sign in
                    </button>
                </div>
            </AccountWebLayout>
        );
    }

    return (
        <AccountWebLayout title="My Credit">
            <AccountPageHeader
                title="My Credit"
                description="Store credit applied at checkout on order summary. Cashback is added after delivery."
                action={
                    <button type="button" className="btn-primary--sm" onClick={() => navigate('/')}>
                        Start shopping
                    </button>
                }
            />

            {error && (
                <div className="account-web-info-block account-web-info-block--error" role="alert">
                    {error}
                </div>
            )}

            <AccountStatStrip
                items={[
                    {
                        label: 'Available',
                        value: loading ? '…' : `₹${balance.toLocaleString('en-IN')}`,
                        tone: 'primary',
                    },
                    {
                        label: 'Earned (lifetime)',
                        value: loading
                            ? '…'
                            : `+₹${(profile?.lifetimeEarned ?? 0).toLocaleString('en-IN')}`,
                        tone: 'credit',
                    },
                    {
                        label: 'Used (lifetime)',
                        value: loading
                            ? '…'
                            : `−₹${(profile?.lifetimeUsed ?? 0).toLocaleString('en-IN')}`,
                        tone: 'debit',
                    },
                ]}
            />

            <AccountTabBar
                ariaLabel="Activity filters"
                activeId={activityTab}
                onChange={setActivityTab}
                tabs={[
                    { id: 'all', label: 'All' },
                    { id: 'earned', label: 'Earned' },
                    { id: 'used', label: 'Used' },
                ]}
            />

            <p className="account-web-meta">
                {loading
                    ? 'Loading…'
                    : `${transactions.length} ${transactions.length === 1 ? 'transaction' : 'transactions'}`}
            </p>

            <div className="account-web-list">
                {loading ? (
                    [1, 2, 3].map((n) => <div key={n} className="account-web-skeleton" aria-hidden />)
                ) : transactions.length === 0 ? (
                    <div className="account-web-empty">
                        <p>No transactions in this category yet.</p>
                    </div>
                ) : (
                    transactions.map((txn) => (
                        <article key={txn.id} className="account-web-row account-web-row--txn">
                            <div className="account-web-row__lead">
                                <div className={`account-web-row__icon account-web-row__icon--${txn.type}`}>
                                    <i
                                        className={`bi bi-${
                                            txn.type === 'credit' ? 'plus-lg' : 'dash-lg'
                                        }`}
                                        aria-hidden
                                    />
                                </div>
                                <div className="account-web-row__main">
                                    <p className="account-web-row__title">{txn.title}</p>
                                    <p className="account-web-row__meta">
                                        {txn.dateLabel}
                                        {txn.orderNumber ? ` · ${txn.orderNumber}` : ''}
                                    </p>
                                    <CreditTransactionDetails items={txn.items} />
                                </div>
                            </div>
                            <span className={`account-web-row__amount account-web-row__amount--${txn.type}`}>
                                {txn.type === 'credit' ? '+' : '−'}₹{txn.amount.toLocaleString('en-IN')}
                            </span>
                        </article>
                    ))
                )}
            </div>

            <section className="account-web-panel">
                <h2 className="account-web-panel__heading">
                    <i className="bi bi-graph-up-arrow" aria-hidden />
                    Ways to earn more credit
                </h2>
                {EARN_INFO.map((item) => (
                    <div key={item.title} className="account-web-benefit">
                        <i className={`bi ${item.icon}`} aria-hidden />
                        <div>
                            <strong>{item.title}</strong>
                            <p>{item.text}</p>
                        </div>
                    </div>
                ))}
            </section>
        </AccountWebLayout>
    );
}
